import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Calendar, ExternalLink } from "lucide-react";
import { Reveal } from "./Reveal";
import Footer from "./Footer";

/**
 * Voeg nieuwe updates simpelweg BOVENAAN de array toe.
 * Elke update heeft: id, datum, title, tags, paragraphs (array van strings of
 * objecten {type:'link', href, label}) en optioneel een `link` onderaan.
 */
const updates = [
  {
    id: "2026-09-verslag-verenigingen",
    datum: "24 september 2026",
    datumIso: "2026-09-24",
    title: "Verslag van de gesprekken met de verenigingen",
    tags: ["Verenigingen", "Verslag"],
    paragraphs: [
      "De afgelopen weken sprak de werkgroep met de verenigingen uit Menaam over hun behoeften en zorgen richting de toekomst. Uit die gesprekken komt een aantal rode draden nadrukkelijk naar voren.",
      "Verenigingsgevoel staat voorop: een gebouw werkt pas als er ruimte is om na afloop samen te komen. Behoud van de Roelengahal wordt door meerdere verenigingen als onmisbaar beschreven — voor sport, toernooien én de kantinefunctie. Een vergrote gymzaal bij de nieuwe school wordt daar niet als volwaardig alternatief voor gezien.",
      "Daarnaast is er behoefte aan goede oefen-, repetitie- en wedstrijdruimtes met aandacht voor akoestiek, plafondhoogte en vloeren. Centrale, droge opslag voor materialen, decor en instrumenten komt breed terug als wens. De ijsbaanfunctie moet behouden blijven, met daarbij de wens tot multifunctioneel gebruik buiten het winterseizoen.",
      "Ten slotte klinkt de vraag naar een laagdrempelige ontmoetingsplek voor het hele dorp — één die niet concurreert met bestaande horeca. Al deze inbreng wordt meegenomen in het accommodatiebeleid van de gemeente en blijft een rol spelen bij de vervolgstappen.",
      "Het volledige verslag is hieronder te downloaden.",
    ],
    link: {
      href: "/downloads/verslag-gesprekken-verenigingen.pdf",
      label: "Download het verslag (PDF)",
    },
  },
  {
    id: "2026-09-september",
    datum: "16 september 2026",
    datumIso: "2026-09-16",
    title:
      "Gesprekken met verenigingen gestart en startnotitie op 24 september",
    tags: ["Verenigingen", "Gemeente Waadhoeke"],
    paragraphs: [
      "Graag informeren we jullie over de laatste ontwikkelingen rond het accommodatieproject in Menaam.",
      "De gesprekken met de verenigingen zijn inmiddels gestart. Hierin brengen we per vereniging de behoeften en wensen voor de toekomst in kaart, zodat dit meegenomen kan worden in de verdere planvorming. Een globaal verslag van deze gesprekken wordt na afloop gedeeld op takomstmenaam.nl.",
      "Om inspiratie op te doen en te leren van ervaringen elders, brengen we daarnaast bezoeken aan soortgelijke projecten elders in Fryslân. Zo kunnen we zien wat werkt, welke valkuilen er zijn en hoe andere dorpen dit hebben aangepakt.",
      "Ook op gemeentelijk niveau is er beweging: het ontwerp 'Omgevingsprogramma gemeente Waadhoeke – Accommodaties voor sport, welzijn en cultuur' heeft van 3 augustus tot en met 13 september ter inzage gelegen. Hierin beschrijft de gemeente hoe ze de komende jaren met accommodatieopgaven omgaat, inclusief harmonisatie van tarieven en afspraken met verenigingen. Ingediende zienswijzen worden nu door het college beoordeeld, waarna het definitieve programma wordt vastgesteld.",
      "Daarnaast is de planning bekend voor een aantal belangrijke vervolgstappen: op 24 september wordt de startnotitie behandeld. Dit document schetst het vervolgproces en de kaders voor de ontwikkeling, en vormt daarmee een belangrijke mijlpaal richting de uiteindelijke besluitvorming. Eind september staat er vanuit de werkgroep ook een gesprek met de gemeente gepland, waarin we de voortgang en de vervolgstappen verder afstemmen.",
      "We houden jullie op de hoogte van de voortgang. Heb je vragen of wil je meedenken? Laat het gerust weten via het contactformulier.",
    ],
    link: {
      href: "https://waadhoeke.bestuurlijkeinformatie.nl/Agenda/Index/496a2d53-b6f6-4130-b414-536d20733527",
      label: "Bekijk de agenda van 24 september",
    },
  },
  {
    id: "2026-08-enquete-uitslag",
    datum: "1 augustus 2026",
    datumIso: "2026-08-01",
    title: "Uitslag enquête: Menaam heeft gesproken",
    tags: ["Enquête", "Uitslag"],
    paragraphs: [
      "208 inwoners/huishoudens deelden hun mening over de toekomst van school, sporthal, gymzaal en gemeenschapsruimte. Dat is 9% van alle volwassen inwoners en 17% van de huishoudens in Menaam.",
      "De boodschap is duidelijk: 94% vindt behoud van de voorzieningen belangrijk, 93% wil dat er nú aan de slag gegaan wordt en 90% staat achter een gedeeld gebouw voor meerdere verenigingen. Er is brede zorg over centralisatie richting Franeker en een sterke roep om snelle, zichtbare vervolgstappen.",
      "Achter de cijfers komen vier thema's telkens terug: ontmoeting — niet een gebouw, behoud van de kernvoorzieningen (basisschool, supermarkt, Roelengahal, ijsbaan), aandacht voor de jeugd (jeugdhonk, hangplek, gymles behouden) en ongeduld met een vraag om daadkracht.",
      "Op de aparte uitslag-pagina lees je alle stellingen, thema's, citaten uit het dorp en de aanbevelingen voor de werkgroep in detail.",
    ],
    link: {
      href: "/enquete-uitslag",
      label: "Bekijk de volledige uitslag",
    },
  },
  {
    id: "2026-04-doarpscafe",
    datum: "16 april 2026",
    datumIso: "2026-04-16",
    title: "Wat zegt ús doarp? — Doarpscafé Menaam",
    tags: ["Doarpscafé", "Terugblik"],
    paragraphs: [
      "Op 16 april 2026 organiseerden we het Doarpscafé in Menaam. Bewoners gingen met elkaar in gesprek over de toekomst van de voorzieningen. De uitkomsten zijn samengevat in een infographic.",
      "De belangrijkste signalen uit die avond: er is brede steun voor behoud én modernisering van de dorpsvoorzieningen, en er leeft urgentie rond school en gymzaal — geen jarenlange vertraging. Aandacht is er specifiek gevraagd voor de toekomstige gebruikers, met name jongeren van 12–18 en dertigers/veertigers.",
      "Openheid voor gedeelde faciliteiten wordt breed gedeeld, mits de Roelengahal blijft. En de boodschap over locatiekeuze was helder: nabijheid en kernfuncties voorop — beter voor het dórp, niet alleen voor de gemeente.",
      "De infographic met alle uitkomsten kun je hieronder bekijken of downloaden.",
    ],
    link: {
      href: "/downloads/wat-zegt-us-doarp.pdf",
      label: "Bekijk de infographic (PDF)",
    },
  },
];

function formatDatum(iso) {
  const maanden = [
    "januari", "februari", "maart", "april", "mei", "juni",
    "juli", "augustus", "september", "oktober", "november", "december",
  ];
  const [y, m, d] = iso.split("-");
  return `${parseInt(d, 10)} ${maanden[parseInt(m, 10) - 1]} ${y}`;
}

export default function UpdatesPage() {
  const location = useLocation();

  useEffect(() => {
    document.title = "Updates | Takomst fan Menaam";
    return () => {
      document.title =
        "Takomst fan Menaam | Nieuwe school & accommodaties in Menaam";
    };
  }, []);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      // Wacht kort tot alle articles gemount zijn
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
  }, [location]);

  return (
    <div className="bg-[#FAF7F2]">
      {/* Top bar */}
      <div className="sticky top-0 z-50 bg-[#1A2E22]/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[68px] flex items-center justify-between">
          <Link
            to="/"
            data-testid="updates-logo"
            className="font-heading text-lg sm:text-xl font-bold text-[#FAF7F2]"
          >
            Takomst fan <span className="text-[#74C69D]">Menaam</span>
          </Link>
          <Link
            to="/"
            data-testid="updates-back-link"
            className="inline-flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-full bg-[#74C69D] text-[#1A2E22] hover:bg-[#8fd4b0] transition-colors"
          >
            <ArrowLeft size={16} /> Naar de plannen
          </Link>
        </div>
      </div>

      {/* HERO */}
      <section className="relative bg-[#1A2E22] overflow-hidden py-24 sm:py-28">
        <div
          aria-hidden="true"
          className="absolute -right-32 top-0 w-[40rem] h-[40rem] rounded-full bg-[#74C69D]/10 blur-3xl pointer-events-none"
        />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <span className="inline-block text-xs font-bold tracking-[0.18em] uppercase mb-6 px-3.5 py-1.5 rounded-full bg-[#74C69D]/15 text-[#74C69D]">
            Nieuws & updates
          </span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-heading text-[#FAF7F2] font-black leading-[1.05] tracking-tight text-4xl sm:text-5xl lg:text-6xl"
          >
            Wat er speelt<br />
            <span className="text-[#74C69D]">in Menaam.</span>
          </motion.h1>
          <p className="mt-6 text-lg leading-relaxed text-[#FAF7F2]/75 max-w-2xl">
            De werkgroep Takomst fan Menaam deelt regelmatig updates over de
            voortgang van het accommodatieproject. Hieronder vind je alle
            berichten in chronologische volgorde — de nieuwste bovenaan.
          </p>
        </div>
      </section>

      {/* UPDATES */}
      <section className="py-20 sm:py-24">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="space-y-10">
            {updates.map((u, i) => (
              <Reveal key={u.id} delay={i * 0.06}>
                <article
                  id={u.id}
                  data-testid={`update-${u.id}`}
                  className="rounded-3xl bg-white border border-[#2D6A4F]/12 p-8 sm:p-10"
                >
                  <div className="flex items-center flex-wrap gap-2 mb-4">
                    <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-[#2D6A4F]">
                      <Calendar size={14} />
                      <time dateTime={u.datumIso}>{formatDatum(u.datumIso)}</time>
                    </span>
                    {u.tags?.map((t) => (
                      <span
                        key={t}
                        data-testid="update-tag"
                        className="text-[0.68rem] font-bold px-2.5 py-0.5 rounded-full bg-[#74C69D]/15 text-[#2D6A4F]"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                  <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-[#1A2E22] mb-5">
                    {u.title}
                  </h2>
                  <div className="space-y-4 text-[#1A2E22]/75 leading-relaxed">
                    {u.paragraphs.map((p, idx) => (
                      <p key={idx} className="m-0">
                        {p}
                      </p>
                    ))}
                  </div>
                  {u.link && (
                    <a
                      href={u.link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`update-${u.id}-link`}
                      className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-[#2D6A4F] hover:text-[#245a41] transition-colors"
                    >
                      {u.link.label} <ExternalLink size={14} />
                    </a>
                  )}
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#2D6A4F]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#FAF7F2] mb-2">
              Blijf op de hoogte
            </h2>
            <p className="text-[#FAF7F2]/75 m-0">
              Wil je updates rechtstreeks in je inbox? Meld je aan voor de nieuwsbrief.
            </p>
          </div>
          <Link
            to="/#meedoen"
            data-testid="updates-cta-meedoen"
            className="group inline-flex items-center gap-2 font-bold text-sm px-7 py-3.5 rounded-full bg-[#FAF7F2] text-[#2D6A4F] hover:bg-white transition-colors shrink-0"
          >
            Meld je aan
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
